import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IPartnerSalesState } from "./types";
import { ErrorType } from "@/shared/types/types";
import { fetchDailyStatistics, fetchDiscountAggregates, fetchMonthlyStatistics, fetchPartnerStatistics } from "./partner-sales-thunk";

const currentDate = new Date()

const initialState: IPartnerSalesState = {
    /* statistics */
    statistics: {
        countClientsLegal: 0,
        countClientsFiz: 0,
        countClients: 0,
        countTransactionsLegal: 0,
        countTransactionsFiz: 0,
        countTransactions: 0,
        bonusTotalFiz: 0,
        bonusTotalLegal: 0,
        bonusTotal: 0,
        totalFiz: 0,
        totalLegal: 0,
        total: 0
    },
    isStatisticsLoading: false,
    isStatisticsFirstLoad: true,
    statisticsError: null,

    /* discounts */
    discountAggregates: {},
    isDiscountAggregatesLoading: false,
    isDiscountAggregatesFirstLoad: true,
    discountAggregatesError: null,

    /* monthly statistics */
    isMonthlyStatisticsLoading: false,
    monthlyStatistics: [],
    monthlyStatisticsError: null,
    isMonthlyStatisticsFirstLoad: true,

    /* daily statistics */
    isDailyStatisticsLoading: false,
    dailyStatistics: [],
    dailyStatisticsError: null,
    isDailyStatisticsFirstLoad: true,

    /* widget controls */
    selectedPartners: null,
    startDate: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
    endDate: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
}

const partnerSales = createSlice({
    name: 'partnerSales',
    initialState,
    reducers: {
        setSelectedPartner: (state, action: PayloadAction<string | null>) => {
            state.selectedPartners = action.payload ? [action.payload] : null
        },
        setStartDate: (state, action: PayloadAction<Date>) => {
            state.startDate = action.payload 
        },
        setEndDate: (state, action: PayloadAction<Date>) => {
            state.endDate = action.payload 
        }
    },
    extraReducers(builder) {
        builder
        .addCase(fetchPartnerStatistics.pending, (state) => {
            state.statisticsError = null
            state.isStatisticsLoading = true
        })
        .addCase(fetchPartnerStatistics.fulfilled, (state, action) => {
            state.statisticsError = null
            state.isStatisticsLoading = false
            state.isStatisticsFirstLoad = false
            state.statistics = action.payload
        })
        .addCase(fetchPartnerStatistics.rejected, (state, action) => {
            state.statisticsError = action.payload as ErrorType ?? null
            state.isStatisticsLoading = false
        })
        .addCase(fetchDiscountAggregates.pending, (state) => {
            state.discountAggregatesError = null
            state.isDiscountAggregatesLoading = true
        })
        .addCase(fetchDiscountAggregates.fulfilled, (state, action) => {
            state.discountAggregatesError = null
            state.isDiscountAggregatesLoading = false
            state.isDiscountAggregatesFirstLoad = false
            state.discountAggregates = action.payload
        })
        .addCase(fetchDiscountAggregates.rejected, (state, action) => {
            state.discountAggregatesError = action.payload as ErrorType ?? null
            state.isDiscountAggregatesLoading = false
        })
        .addCase(fetchDailyStatistics.pending, (state) => {
            state.dailyStatisticsError = null
            state.isDailyStatisticsLoading = true
        })
        .addCase(fetchDailyStatistics.fulfilled, (state, action) => {
            state.discountAggregatesError = null
            state.isDailyStatisticsLoading = false
            state.isDailyStatisticsFirstLoad = false
            state.dailyStatistics = action.payload
        })
        .addCase(fetchDailyStatistics.rejected, (state, action) => {
            state.dailyStatisticsError = action.payload as ErrorType ?? null
            state.isDailyStatisticsLoading = false
        })
        .addCase(fetchMonthlyStatistics.pending, (state) => {
            state.dailyStatisticsError = null
            state.isDailyStatisticsLoading = true
        })
        .addCase(fetchMonthlyStatistics.fulfilled, (state, action) => {
            state.monthlyStatisticsError = null
            state.isMonthlyStatisticsLoading = false
            state.isMonthlyStatisticsFirstLoad = false
            state.monthlyStatistics = action.payload
        })
        .addCase(fetchMonthlyStatistics.rejected, (state, action) => {
            state.monthlyStatisticsError = action.payload as ErrorType ?? null
            state.isMonthlyStatisticsLoading = false
        })
    },
})

export default partnerSales.reducer
export const { setEndDate, setSelectedPartner, setStartDate } = partnerSales.actions